<template>
    <v-container fluid class="pa-0">
        <v-row>
            <v-col>
                <v-card>
                    <v-card-title> Samples </v-card-title>
                </v-card>
                <v-data-table
                    :headers="headers"
                    :items="requests"
                    class="elevation-1"
                    :loading="isLoading"
                    :items-per-page="25"
                    :search="search"
                    no-results-text="No matching records found (you may have to clear the protocol or status filter in the table header)"
                    :footer-props="{
                        itemsPerPageOptions: [25, 50, 100, -1],
                    }"
                >
                    <template v-slot:item.request_registry_id="{ item, value: modelValue }">
                        <a :href="item.request_url" target="_blank">{{ modelValue }}</a>
                    </template>
                    <template v-slot:item.src_experiment_code="{ item }">
                        <div v-for="(exp, index) in item.src_experiment_code" :key="index">
                            <a :href="item.entry_url[index]" target="_blank">
                                {{ exp }}
                            </a>
                        </div>
                    </template>

                    <!-- Filter 'Protocols' column -->
                    <template v-slot:header.name>
                        <div style="max-width: 200px">
                            <v-autocomplete
                                class="text-same-as-other-headers"
                                v-model="protocolId"
                                @update:model-value="onFilterChange"
                                :items="protocolOptions"
                                item-title="name"
                                item-value="protocol_id"
                                clearable
                                placeholder="Protocols (All)"
                            >
                            </v-autocomplete>
                        </div>
                    </template>

                    <!-- 'Actions' column -->
                    <template v-slot:item.actions="{ item, value }">
                        <v-row>
                            <v-icon-btn color="primary" @click="download(item.request_registry_id)">
                                <v-icon>$download</v-icon>
                            </v-icon-btn>
                        </v-row>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>

        <req-dialog v-model="data" :show="isOpen" @onSave="saved"> </req-dialog>
    </v-container>
</template>

<script>
import { defineComponent } from "vue";
import { mapActions, mapGetters } from "vuex";
import { downloadRequestExcel } from "@/services/api.services";
import ReqDialog from "../Requests/ReqDialog";
import { downloadFile } from "@/lib/downloadFileUtil";
import { withLoadingScreen } from "@/lib/loading";

const defaultProtocols = [
    { name: "All Analytical", protocol_id: "analytical" },
    { name: "All Molecular", protocol_id: "molecular" },
];

export default defineComponent({
    name: "SamplesList",
    components: { ReqDialog },

    props: {
        search: {
            type: String,
            default: "2",
        },
        tableHeaderColor: {
            type: String,
            default: "",
        },
    },

    async mounted() {
        await withLoadingScreen(async () => {
            await this.initialize();
            await this.fetchRequests({ statusId: "Samples", protocolId: this.protocolId });
            this.protocolOptions = defaultProtocols.concat(this.protocols);
        });
    },

    computed: {
        ...mapGetters("requests", ["isLoading", "protocols", "requests", "statuses"]),
    },

    methods: {
        ...mapActions("requests", ["fetchRequests", "initialize"]),
        onFilterChange() {
            this.fetchRequests({ statusId: this.status, protocolId: this.protocolId });
        },
        saved: function (data) {
            this.isOpen = false;
        },
        link: function (data) {
            this.data = data;
            this.isOpen = true;
        },
        async download(requestCode) {
            const response = await downloadRequestExcel(requestCode);
            downloadFile(`${requestCode}-Report.xlsx`, response);
        },
        edit(requestCode) {
            this.$router.push({ name: "BuildPlate", params: { requestCode } });
        },
    },

    data() {
        return {
            data: null,
            isOpen: false,
            headers: [
                {
                    title: "Req Id",
                    value: "request_registry_id",
                    align: "left",
                    sortable: true,
                    width: "84",
                },
                {
                    title: "Source EXP",
                    value: "src_experiment_code",
                    align: "left",
                    sortable: true,
                    width: "140",
                },
                {
                    title: "# Samples",
                    value: "number_of_samples",
                    align: "center",
                    width: "50",
                },
                {
                    title: "Date Submitted",
                    value: "created_date",
                    align: "left",
                    width: "140",
                },
                {
                    title: "Date Required",
                    value: "date_required",
                    align: "left",
                    width: "140",
                },
                {
                    title: "Protocol",
                    value: "name",
                    align: "left",
                    sortable: false,
                    width: "200",
                },
                {
                    title: "Actions",
                    value: "actions",
                    width: "50",
                },
            ],
            rowsPerPageItems: [20, 40, 60],
            pagination: {
                rowsPerPage: 20,
            },
            status: "Samples",
            protocolId: null,
            protocolOptions: [],
        };
    },
});
</script>

<style>
.text-same-as-other-headers {
    font-size: 0.75rem !important;
}
</style>
